import { select } from 'd3-selection';

const animationDelay = (obj: any) => (obj.animate && obj.animate.enabled && obj.animate.delay ? obj.animate.delay : 0);
const animationDuration = (obj: any) => (obj.animate && obj.animate.enabled && obj.animate.duration ? obj.animate.duration : 0);

const attrIfAnimated = (node: any, obj: any, key: string, name?: string, unit: string = '') => {
  // no animation information available
  if (!obj.animate || !obj.animate.enabled) return;

  if (obj.animate[key] === undefined || (typeof obj.animate[key] === 'number' && Number.isNaN(obj.animate[key]))) {
    return;
  }

  node.attr(name || key, obj.animate[key] + unit);
};

const attrIfStatic = (node: any, obj: any, force: boolean = false, key: string, name?: string, unit: string = '') => {
  // remove attr
  if (obj[key] === undefined || (typeof obj[key] === 'number' && Number.isNaN(obj[key]))) {
    node.attr(name || key, null);
    return;
  }

  // this attr will be animated
  if (!force && obj.animate && obj.animate.enabled && obj.animate[key] !== undefined) return;

  // try to use the animation result
  if (obj.animate && !obj.animate.enabled && obj.animate[key] !== undefined) {
    node.attr(name || key, obj.animate[key] + unit);
  } else {
    node.attr(name || key, obj[key] + unit);
  }
};

const styleIfAnimated = (node: any, obj: any, key: string, name?: string, unit: string = '') => {
  // no animation information available
  if (!obj.animate?.enabled) return;

  if (obj.animate[key] === undefined || (typeof obj.animate[key] === 'number' && Number.isNaN(obj.animate[key]))) {
    return;
  }

  // @ts-ignore
  const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
  if (isIE11) return;

  node.style(name || key, obj.animate[key] + unit);
};

const styleIfStatic = (node: any, obj: any, force: boolean = false, key: string, name?: string, unit: string = '') => {
  // remove style
  if ((obj[key] === undefined && key !== 'transformForce') || (typeof obj[key] === 'number' && Number.isNaN(obj[key]))) {
    node.style(name || key, null);
    return;
  }

  // @ts-ignore
  const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;

  // this attr will be animated
  if (!force && obj.animate?.enabled && !isIE11 && obj.animate[key] !== undefined) return;

  // animation disabled, use final result
  if ((obj.animate?.enabled === false || isIE11) && obj.animate[key] !== undefined) {
    node.style(name || key, obj.animate[key] + unit);
    return;
  }

  if (key === 'transformForce') {
    if (obj[key]) {
      const preStyle = node
        .style(name || key)
        .split(/ (?=rotate)/)
        .find((part) => part.includes('translate'));
      node.style(name || key, preStyle + ' ' + obj[key] + unit);
    }
  } else {
    node.style(name || key, obj[key] + unit);
  }
};

const callbackFn = (event: any, obj: any, name: string, ...arg) => {
  if (!obj.callbacks) return;
  if (!obj.callbacks[name]) return;
  obj.callbacks[name](event, obj, ...arg);
};

const applyCallbacks = (selection: any) => {
  selection
    .on('click', (event: any, obj: any) => callbackFn(event, obj, 'click', event.currentTarget))
    .on('mouseleave', (event: any, obj: any) => callbackFn(event, obj, 'leave', event.currentTarget))
    .on('mouseover', (event: any, obj: any) => callbackFn(event, obj, 'hover', event.currentTarget))
    .on('mouseout', (event: any, obj: any) => callbackFn(event, obj, 'out', event.currentTarget));
};

export const overlayText = (overlay: any, data: any, selector?: string) => {

  const selection = (selector ? overlay.element.select(selector) : overlay.element).selectAll('div.quill-container').data(data);
  selection.interrupt();

  const applyAttr = (selection1: any, force: boolean = false) => {
    selection1.each((obj: any, idx: number, nodes: any) => {
      const node = select(nodes[idx]);

      const transition = node
        .transition()
        .delay((obj1: any) => idx * animationDelay(obj1))
        .duration((obj1: any) => animationDuration(obj1));

      // node.text((obj1: any) => obj1.text);
      node.html((obj1: any) => obj1.html);

      if (obj.class) {
        for (const [className, enabled] of Object.entries<boolean>(obj.class)) {
          node.classed(className, enabled);
        }
      } else {
        node.attr('class', null);
        node.classed('quill-container', true);
      }

      // attrIfStatic(node, obj, force, 'class');

      // applyStatic(node, obj, 'x', 'top', 'px');
      // applyStatic(node, obj, 'y', 'left', 'px');
      styleIfStatic(node, obj, force, 'color');
      styleIfStatic(node, obj, force, 'opacity');
      styleIfStatic(node, obj, force, 'flex');
      styleIfStatic(node, obj, force, 'transform');
      styleIfStatic(node, obj, force, 'transformForce', 'transform');
      styleIfStatic(node, obj, force, 'transformOrigin', 'transform-origin');
      styleIfStatic(node, obj, force, 'writingMode', 'writing-mode');
      styleIfStatic(node, obj, force, 'width', undefined, 'px');
      styleIfStatic(node, obj, force, 'height', undefined, 'px');

      styleIfStatic(node, obj, force, 'justifyContent', 'justify-content');
      styleIfStatic(node, obj, force, 'alignItems', 'align-items');

      // DEBUG
      styleIfStatic(node, obj, force, 'outline', 'outline');

      styleIfAnimated(transition, obj, 'color');
      styleIfAnimated(transition, obj, 'opacity');
      styleIfAnimated(transition, obj, 'flex');
      styleIfAnimated(transition, obj, 'transform');
      styleIfAnimated(transition, obj, 'transformOrigin', 'transform-origin');
      styleIfAnimated(transition, obj, 'writingMode', 'writing-mode');
      styleIfAnimated(transition, obj, 'width', undefined, 'px');
      styleIfAnimated(transition, obj, 'height', undefined, 'px');

      styleIfAnimated(transition, obj, 'justifyContent', 'justify-content');
      styleIfAnimated(transition, obj, 'alignItems', 'align-items');

      // if (obj.callbacks && obj.callbacks.click) {
      //   node.style('cursor', 'pointer');
      //   node.style('pointer-events', 'all');
      // }
    });
  };

  // create new elements
  selection
    .enter()
    .append('div')
    .attr('class', 'quill-container')
    .attr('lang', 'de')
    // .attr('font-family', `'Roboto', sans-serif`)
    // .style('pointer-events', (obj: any) => obj.pointerEvents)
    .call(applyAttr, true)
    .call(applyCallbacks);

  // update existing elements
  selection.call(applyAttr);

  // remove expired elements
  selection.exit().remove();
};

export const overlayImage = (overlay: any, data: any, selector?: string) => {
  const selection = (selector ? overlay.element.select(selector) : overlay.element).selectAll('div.image-container').data(data);
  selection.interrupt();

  const applyAttr = (selection1: any, force: boolean = false) => {
    selection1.each((obj: any, idx: number, nodes: any) => {
      const node = select(nodes[idx]);
      // console.log('🚀 ~ node:', node.node());
      const transition = node
        .transition()
        .delay((obj1: any) => idx * animationDelay(obj1))
        .duration((obj1: any) => animationDuration(obj1));

      // node.text((obj1: any) => obj1.text);
      node.html((obj1: any) => obj1.html);
      // console.log('🚀 ~ obj:', obj);

      // node.attr('class', null); // Restart
      if (obj.class) {
        for (const [className, enabled] of Object.entries<boolean>(obj.class)) {
          node.classed(className, enabled);
        }
      } else {
        node.attr('class', null);
        node.classed('image-container', true);
      }

      // attrIfStatic(node, obj, force, 'class');

      // applyStatic(node, obj, 'x', 'top', 'px');
      // applyStatic(node, obj, 'y', 'left', 'px');
      styleIfStatic(node, obj, force, 'color');
      styleIfStatic(node, obj, force, 'opacity');
      styleIfStatic(node, obj, force, 'flex');
      styleIfStatic(node, obj, force, 'transform');
      styleIfStatic(node, obj, force, 'transformForce', 'transform');
      styleIfStatic(node, obj, force, 'transformOrigin', 'transform-origin');
      styleIfStatic(node, obj, force, 'writingMode', 'writing-mode');
      styleIfStatic(node, obj, force, 'width', undefined, 'px');
      styleIfStatic(node, obj, force, 'height', undefined, 'px');

      styleIfStatic(node, obj, force, 'justifyContent', 'justify-content');
      styleIfStatic(node, obj, force, 'alignItems', 'align-items');

      styleIfAnimated(transition, obj, 'color');
      styleIfAnimated(transition, obj, 'opacity');
      styleIfAnimated(transition, obj, 'flex');
      styleIfAnimated(transition, obj, 'transform');
      styleIfAnimated(transition, obj, 'transformOrigin', 'transform-origin');
      styleIfAnimated(transition, obj, 'writingMode', 'writing-mode');
      styleIfAnimated(transition, obj, 'width', undefined, 'px');
      styleIfAnimated(transition, obj, 'height', undefined, 'px');

      styleIfAnimated(transition, obj, 'justifyContent', 'justify-content');
      styleIfAnimated(transition, obj, 'alignItems', 'align-items');

      // if (obj.callbacks && obj.callbacks.click) {
      //   node.style('cursor', 'pointer');
      //   node.style('pointer-events', 'all');
      // }
    });
  };

  // create new elements
  selection
    .enter()
    .append('div')
    .attr('class', 'image-container')
    // .attr('lang', 'de')
    // .attr('font-family', `'Roboto', sans-serif`)
    // .style('pointer-events', (obj: any) => obj.pointerEvents)
    .call(applyAttr, true)
    .call(applyCallbacks);

  // update existing elements
  selection.call(applyAttr);

  // remove expired elements
  selection.exit().remove();
};
